import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Lock, LogOut, Mail, RotateCcw, Search, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "weave_admin_token";

export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const auth = (t) => ({ Authorization: `Bearer ${t || token}` });

  const load = async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/catch-wave/submissions?limit=500`, { headers: auth(t) });
      setItems(data.items || []);
      sessionStorage.setItem(TOKEN_KEY, t || token);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) toast.error("Invalid admin token.");
      else toast.error("Couldn't load submissions.");
      setItems(null);
      sessionStorage.removeItem(TOKEN_KEY);
    } finally { setLoading(false); }
  };

  const logout = () => { setToken(""); setItems(null); sessionStorage.removeItem(TOKEN_KEY); };

  const resetEmail = async (email) => {
    if (!window.confirm(`Reset ${email}? They'll be able to request a fresh free scan.`)) return;
    try {
      await axios.post(`${API}/catch-wave/admin/reset`, { email }, { headers: auth() });
      toast.success(`Reset ${email}`);
      load();
    } catch { toast.error("Reset failed"); }
  };

  const resendScan = async (email) => {
    try {
      await axios.post(`${API}/catch-wave/resend`, { email });
      toast.success(`Re-sent scan to ${email}`);
    } catch { toast.error("Resend failed"); }
  };

  const exportCSV = () => {
    if (!items?.length) return;
    const cols = ["created_at","name","email","website","personal_linkedin","business_linkedin","industry","consent_marketing","consent_processing","consent_ai_disclaimer","superseded","challenge","postmark_message_id","analysis_headline"];
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = items.map((it) => cols.map((c) => {
      if (c === "analysis_headline") return esc(it.analysis?.headline);
      return esc(it[c]);
    }).join(","));
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `catch-the-wave-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const filtered = (items || []).filter((it) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [it.name, it.email, it.website, it.industry, it.analysis?.headline].some((f) => (f || "").toLowerCase().includes(q));
  });

  // --- Login view ---
  if (!items) {
    return (
      <div className="min-h-screen font-body bg-[var(--cream)] flex items-center justify-center px-6">
        <div className="w-full max-w-md card-sand p-8 md:p-10" data-testid="admin-login">
          <div className="flex items-center gap-2 text-xs tracking-[0.3em] font-bold text-[var(--coral)]"><Lock className="w-4 h-4"/> ADMIN</div>
          <h1 className="font-display font-black text-4xl mt-3 text-[var(--ink)]">Catch the Wave · submissions</h1>
          <p className="mt-3 text-sm text-[var(--ink)]/70">Paste your admin token to view the database.</p>
          <form onSubmit={(e) => { e.preventDefault(); if (token.trim()) load(token.trim()); }} className="mt-6 space-y-4">
            <Input data-testid="admin-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin token" autoFocus required />
            <Button type="submit" disabled={loading} className="btn-coral w-full" data-testid="admin-login-submit">{loading ? "Checking…" : "Unlock"}</Button>
          </form>
        </div>
      </div>
    );
  }

  // --- Dashboard view ---
  return (
    <div className="min-h-screen font-body bg-[var(--cream)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-end gap-4 justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs tracking-[0.3em] font-bold text-[var(--coral)]"><Users className="w-4 h-4"/> ADMIN · SUBMISSIONS</div>
            <h1 className="font-display font-black text-4xl md:text-5xl mt-2 text-[var(--ink)]">{items.length} scan{items.length === 1 ? "" : "s"} captured</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={exportCSV} disabled={!items.length} className="btn-ghost" data-testid="admin-export"><Download className="w-4 h-4 mr-2"/>Export CSV</Button>
            <Button onClick={logout} variant="ghost" className="text-[var(--ink)]"><LogOut className="w-4 h-4 mr-2"/>Log out</Button>
          </div>
        </div>

        <div className="mt-6 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink)]/40"/>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, industry, headline…" className="pl-9" data-testid="admin-search" />
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border-2 border-[var(--ink)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[var(--sand)] text-[var(--ink)] text-left">
              <tr>
                <th className="px-4 py-3 font-bold">Date</th>
                <th className="px-4 py-3 font-bold">Name</th>
                <th className="px-4 py-3 font-bold">Email</th>
                <th className="px-4 py-3 font-bold">Industry</th>
                <th className="px-4 py-3 font-bold">Marketing</th>
                <th className="px-4 py-3 font-bold">Headline</th>
                <th className="px-4 py-3 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--ink)]/50">No submissions yet.</td></tr>
              )}
              {filtered.map((it) => (
                <tr key={it.id} className={`border-t border-[var(--ink)]/10 ${it.superseded ? "opacity-50" : ""}`} data-testid={`row-${it.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(it.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" })}</td>
                  <td className="px-4 py-3">{it.name}</td>
                  <td className="px-4 py-3"><a href={`mailto:${it.email}`} className="underline">{it.email}</a></td>
                  <td className="px-4 py-3">{it.industry || <span className="text-[var(--ink)]/40">—</span>}</td>
                  <td className="px-4 py-3">{it.consent_marketing ? <span className="text-xs font-bold text-[var(--mint)] bg-[var(--mint)]/20 px-2 py-1 rounded-full">OPT-IN</span> : <span className="text-xs text-[var(--ink)]/40">no</span>}</td>
                  <td className="px-4 py-3 max-w-xs truncate" title={it.analysis?.headline}>{it.analysis?.headline}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <button onClick={() => resendScan(it.email)} className="text-[var(--ink)]/70 hover:text-[var(--ink)] p-1.5" title="Resend email"><Mail className="w-4 h-4"/></button>
                    <button onClick={() => resetEmail(it.email)} className="text-[var(--ink)]/70 hover:text-[var(--coral)] p-1.5" title="Reset — allow another free scan"><RotateCcw className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-xs text-[var(--ink)]/50">Showing {filtered.length} of {items.length}. Superseded (reset) rows are greyed.</p>
      </div>
    </div>
  );
}
